import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Upload, FileText, FolderOpen, Send, Paperclip, File, Star, Trash2, Download, ChevronRight, ChevronLeft, MessageSquare, Search, SlidersHorizontal, Folder, FolderClosed, Shield, BookOpen, Receipt, Users, Briefcase, Bold, Italic } from 'lucide-react';
import { supabase } from '../supabaseClient';
import './CaseWorkspace.css';

// Folder definitions
const FOLDERS = [
    { id: 'all',            label: 'All Files',              icon: FolderOpen,  color: '#5b8df2', bg: '#eef3ff', description: 'Every file in this case' },
    { id: 'evidence',       label: 'Evidence',               icon: Shield,      color: '#e86c3a', bg: '#fff4ee', description: 'Photos, videos, forensic reports' },
    { id: 'case_documents', label: 'Case Documents',         icon: Briefcase,   color: '#2f80ed', bg: '#edf5ff', description: 'Pleadings, motions, orders, judgments' },
    { id: 'contracts',      label: 'Contracts & Agreements', icon: BookOpen,    color: '#7c4ddb', bg: '#f4f0ff', description: 'Contracts, NDAs, settlements' },
    { id: 'correspondence', label: 'Correspondence',         icon: MessageSquare, color: '#0ba17d', bg: '#edfaf5', description: 'Letters, emails, notices' },
    { id: 'financial',      label: 'Financial Records',      icon: Receipt,     color: '#c4960f', bg: '#fffaeb', description: 'Invoices, bank statements, receipts' },
    { id: 'witness',        label: 'Witness Statements',     icon: Users,       color: '#d14d8f', bg: '#fff0f7', description: 'Affidavits, depositions, testimonies' },
];

function getFolderById(id) {
    return FOLDERS.find(f => f.id === id) || FOLDERS[0];
}

function timeAgo(ts) {
    if (!ts) return '';
    const diff = Date.now() - new Date(ts).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return new Date(ts).toLocaleDateString();
}

function getTypeClass(type) {
    if (!type) return '';
    if (type.includes('pdf')) return 'type-pdf';
    if (type.includes('image')) return 'type-img';
    if (type.includes('word') || type.includes('doc')) return 'type-doc';
    if (type.includes('sheet') || type.includes('excel') || type.includes('csv')) return 'type-sheet';
    return '';
}

function formatBytes(bytes) {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
}

function inferMimeTypeFromName(fileName = '') {
    const ext = String(fileName).split('.').pop()?.toLowerCase();
    if (!ext) return null;

    const map = {
        pdf: 'application/pdf',
        doc: 'application/msword',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        xls: 'application/vnd.ms-excel',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        csv: 'text/csv',
        txt: 'text/plain',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        webp: 'image/webp',
        gif: 'image/gif',
        mp4: 'video/mp4',
    };

    return map[ext] || null;
}

function escapeHtml(text) {
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function toEditorHtml(content) {
    if (!content) return '';
    const raw = String(content);
    const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(raw);
    if (looksLikeHtml) return raw;
    return escapeHtml(raw).replace(/\n/g, '<br>');
}

function isNoteContentEmpty(content = '') {
    const plain = String(content)
        .replace(/<br\s*\/?\s*>/gi, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/<[^>]*>/g, '')
        .trim();
    return plain.length === 0;
}

function normalizeBlockValue(value) {
    const cleaned = String(value || '').toLowerCase().replace(/[<>]/g, '').trim();
    if (cleaned === 'div') return 'p';
    if (['p', 'h1', 'h2', 'h3'].includes(cleaned)) return cleaned;
    return 'p';
}

function normalizeFontSizeValue(value) {
    const cleaned = String(value || '').trim();
    if (/^[1-7]$/.test(cleaned)) return cleaned;
    return '3';
}

function MiniAvatar({ name, photoUrl }) {
    const initials = (name || '?').split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();
    const [err, setErr] = React.useState(false);
    return (
        <div className="cw-mini-avatar">
            {photoUrl && !err
                ? <img src={photoUrl} alt={name} onError={() => setErr(true)} />
                : <span>{initials}</span>}
        </div>
    );
}

export default function CaseWorkspace({ relationship, user, userRole, otherUser, onBack }) {
    const [activeView, setActiveView] = useState('files');
    const [activeFolder, setActiveFolder] = useState('all');
    const [fileSearch, setFileSearch] = useState('');
    const [files, setFiles] = useState([]);
    const [fileCount, setFileCount] = useState(0);
    const [notes, setNotes] = useState('');
    const [editorSeed, setEditorSeed] = useState('');
    const [notesEmpty, setNotesEmpty] = useState(true);
    const [isNoteFocused, setIsNoteFocused] = useState(false);
    const [editorFormat, setEditorFormat] = useState({ bold: false, italic: false, block: 'p', fontSize: '3' });
    const [notesSaved, setNotesSaved] = useState(true);
    const [savingNotes, setSavingNotes] = useState(false);
    const [chatOpen, setChatOpen] = useState(true);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatText, setChatText] = useState('');
    const [uploading, setUploading] = useState(false);
    const [showRating, setShowRating] = useState(false);
    const [pendingDeleteFile, setPendingDeleteFile] = useState(null);
    const [ratingStars, setRatingStars] = useState(0);
    const [ratingHover, setRatingHover] = useState(0);
    const [ratingReview, setRatingReview] = useState('');
    const [existingRating, setExistingRating] = useState(null);
    const [myProfile, setMyProfile] = useState(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadFolder, setUploadFolder] = useState('case_documents');
    const [showChatAttachModal, setShowChatAttachModal] = useState(false);
    const [chatAttachMode, setChatAttachMode] = useState('choose');
    const [chatAttachSearch, setChatAttachSearch] = useState('');
    const [selectedChatCaseFileId, setSelectedChatCaseFileId] = useState(null);
    const [chatSendingCaseFile, setChatSendingCaseFile] = useState(false);
    const [chatFileActions, setChatFileActions] = useState(null);
    const [chatSaveFolder, setChatSaveFolder] = useState('case_documents');
    const [savingChatFileToCase, setSavingChatFileToCase] = useState(false);
    const bottomRef = useRef(null);
    const fileInputRef = useRef(null);
    const chatFileRef = useRef(null);
    const noteEditorRef = useRef(null);

    const otherName = otherUser?.full_name || otherUser?.email?.split('@')[0] || 'User';
    const otherRoleLabel = userRole === 'client' ? 'Lawyer' : 'Client';

    const folderFiles = activeFolder === 'all' ? files : files.filter(f => f.folder === activeFolder);
    const filteredFiles = folderFiles.filter((f) =>
        (f.file_name || '').toLowerCase().includes(fileSearch.toLowerCase())
    );
    const chatAttachFiles = files.filter((f) =>
        (f.file_name || '').toLowerCase().includes(chatAttachSearch.toLowerCase())
    );
    const selectedChatCaseFile = files.find((f) => String(f.id) === String(selectedChatCaseFileId));

    const folderCounts = FOLDERS.reduce((acc, f) => {
        acc[f.id] = f.id === 'all' ? files.length : files.filter(x => x.folder === f.id).length;
        return acc;
    }, {});

    useEffect(() => {
        if (!relationship?.id) return;
        loadFiles(); loadNotes(); loadChat();
        if (userRole === 'client') loadExistingRating();
        supabase.from('user_profiles').select('full_name, photo_url').eq('id', user.id).single()
            .then(({ data }) => setMyProfile(data));
    }, [relationship?.id]);

    useEffect(() => {
        if (!relationship?.id) return;
        const channel = supabase.channel(`cw-${relationship.id}`)
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'case_messages', filter: `relationship_id=eq.${relationship.id}` }, () => loadChat())
            .subscribe();
        return () => supabase.removeChannel(channel);
    }, [relationship?.id]);

    useEffect(() => {
        if (activeView !== 'notes' || !noteEditorRef.current) return;
        noteEditorRef.current.innerHTML = editorSeed;
        updateEditorFormatState();
    }, [activeView, editorSeed]);

    useEffect(() => {
        if (activeView !== 'notes') return;
        const onSelectionChange = () => updateEditorFormatState();
        document.addEventListener('selectionchange', onSelectionChange);
        return () => document.removeEventListener('selectionchange', onSelectionChange);
    }, [activeView]);

    const loadFiles = async () => {
        const { data } = await supabase.from('case_files').select('*').eq('relationship_id', relationship.id).order('created_at', { ascending: false });
        setFiles(data || []);
        setFileCount((data || []).length);
    };

    const loadNotes = async () => {
        const { data } = await supabase.from('case_notes').select('content').eq('relationship_id', relationship.id).eq('author_id', user.id).single();
        const loadedContent = toEditorHtml(data?.content || '');
        setNotes(loadedContent);
        setEditorSeed(loadedContent);
        setNotesEmpty(isNoteContentEmpty(loadedContent));
        setNotesSaved(true);
    };

    const saveNotes = async () => {
        setSavingNotes(true);
        const { data: existing } = await supabase.from('case_notes').select('id').eq('relationship_id', relationship.id).eq('author_id', user.id).single();
        if (existing) {
            await supabase.from('case_notes').update({ content: notes, updated_at: new Date().toISOString() }).eq('id', existing.id);
        } else {
            await supabase.from('case_notes').insert([{ relationship_id: relationship.id, author_id: user.id, content: notes }]);
        }
        setSavingNotes(false);
        setNotesSaved(true);
    };

    const syncNotesFromEditor = () => {
        const editor = noteEditorRef.current;
        if (!editor) return;
        const html = editor.innerHTML === '<br>' ? '' : editor.innerHTML;
        setNotes(html);
        setNotesEmpty(isNoteContentEmpty(html));
        setNotesSaved(false);
    };

    const updateEditorFormatState = () => {
        const editor = noteEditorRef.current;
        if (!editor) return;

        const selection = window.getSelection();
        const anchorNode = selection?.anchorNode;
        const anchorElement = anchorNode?.nodeType === 3 ? anchorNode.parentElement : anchorNode;
        const isInsideEditor = !!anchorElement && editor.contains(anchorElement);
        const isEditorFocused = document.activeElement === editor;

        if (!isInsideEditor && !isEditorFocused) return;

        let nextBold = false;
        let nextItalic = false;
        let nextBlock = 'p';
        let nextFontSize = '3';

        try {
            nextBold = !!document.queryCommandState('bold');
            nextItalic = !!document.queryCommandState('italic');
            nextBlock = normalizeBlockValue(document.queryCommandValue('formatBlock'));
            nextFontSize = normalizeFontSizeValue(document.queryCommandValue('fontSize'));
        } catch {
            // Ignore command-state failures on unsupported browsers.
        }

        setEditorFormat({ bold: nextBold, italic: nextItalic, block: nextBlock, fontSize: nextFontSize });
    };

    const runEditorCommand = (command, value = null) => {
        if (!noteEditorRef.current) return;
        noteEditorRef.current.focus();
        document.execCommand(command, false, value);
        syncNotesFromEditor();
        updateEditorFormatState();
    };

    const applyBlockType = (value) => {
        const normalized = normalizeBlockValue(value);
        runEditorCommand('formatBlock', normalized === 'p' ? 'P' : normalized.toUpperCase());
    };

    const applyTextSize = (sizeLevel) => {
        if (!noteEditorRef.current) return;
        noteEditorRef.current.focus();
        document.execCommand('styleWithCSS', false, true);
        document.execCommand('fontSize', false, normalizeFontSizeValue(sizeLevel));
        syncNotesFromEditor();
        updateEditorFormatState();
    };

    const loadChat = async () => {
        const { data } = await supabase.from('case_messages').select('*').eq('relationship_id', relationship.id).order('created_at', { ascending: true });
        setChatMessages(data || []);
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
    };

    const sendChat = async (content, fileUrl = null, fileName = null) => {
        if (!content?.trim() && !fileUrl) return;
        await supabase.from('case_messages').insert([{
            relationship_id: relationship.id, sender_id: user.id,
            content: content?.trim() || null, file_url: fileUrl, file_name: fileName,
        }]);
        setChatText('');
        await loadChat();
    };

    const uploadCaseFile = async (file, folderId) => {
        if (!file || !folderId || folderId === 'all') return;
        setUploading(true);
        try {
            const ext = file.name.split('.').pop();
            const path = `cases/${relationship.id}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
            const { error } = await supabase.storage.from('case-files').upload(path, file, { upsert: true });
            if (error) throw error;
            const { data: { publicUrl } } = supabase.storage.from('case-files').getPublicUrl(path);
            await supabase.from('case_files').insert([{
                relationship_id: relationship.id, uploaded_by: user.id,
                file_name: file.name, file_url: publicUrl,
                file_size: file.size, file_type: file.type,
                folder: folderId,
            }]);
            await loadFiles();
            setActiveFolder(folderId);
        } catch (err) {
            alert('Upload failed: ' + err.message);
        } finally {
            setUploading(false);
        }
    };

    const openUploadModal = () => {
        setUploadFolder(activeFolder === 'all' ? 'case_documents' : activeFolder);
        setShowUploadModal(true);
    };

    const triggerUploadFilePicker = () => {
        fileInputRef.current?.click();
    };

    const handleFileSelected = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const targetFolder = uploadFolder === 'all' ? 'case_documents' : uploadFolder;
        setShowUploadModal(false);
        await uploadCaseFile(file, targetFolder);

        e.target.value = '';
    };

    const uploadChatFile = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const ext = file.name.split('.').pop();
            const path = `cases/${relationship.id}/chat/${Date.now()}.${ext}`;
            const { error } = await supabase.storage.from('case-files').upload(path, file, { upsert: true });
            if (error) throw error;
            const { data: { publicUrl } } = supabase.storage.from('case-files').getPublicUrl(path);
            await sendChat(null, publicUrl, file.name);
        } catch (err) {
            alert('Upload failed: ' + err.message);
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const downloadFile = async (fileUrl, fileName = 'file') => {
        if (!fileUrl) return;
        try {
            const response = await fetch(fileUrl);
            if (!response.ok) throw new Error('Unable to fetch file');
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        } catch {
            // Fallback for cross-origin or blocked fetch cases.
            const link = document.createElement('a');
            link.href = fileUrl;
            link.download = fileName;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const openChatAttachModal = () => {
        setChatAttachMode('choose');
        setChatAttachSearch('');
        setSelectedChatCaseFileId(null);
        setShowChatAttachModal(true);
    };

    const openLocalChatUpload = () => {
        setShowChatAttachModal(false);
        setTimeout(() => chatFileRef.current?.click(), 0);
    };

    const sendExistingCaseFileToChat = async () => {
        if (!selectedChatCaseFile) return;
        setChatSendingCaseFile(true);
        try {
            await sendChat(null, selectedChatCaseFile.file_url, selectedChatCaseFile.file_name);
            setShowChatAttachModal(false);
            setChatAttachMode('choose');
            setChatAttachSearch('');
            setSelectedChatCaseFileId(null);
        } finally {
            setChatSendingCaseFile(false);
        }
    };

    const openChatFileActions = (msg) => {
        const defaultFolder = activeFolder === 'all' ? 'case_documents' : activeFolder;
        setChatSaveFolder(defaultFolder);
        setChatFileActions({
            fileUrl: msg.file_url,
            fileName: msg.file_name || 'Case file',
        });
    };

    const downloadChatFileToPc = () => {
        if (!chatFileActions?.fileUrl) return;
        downloadFile(chatFileActions.fileUrl, chatFileActions.fileName || 'file');
        setChatFileActions(null);
    };

    const saveChatFileToCaseFiles = async () => {
        if (!chatFileActions?.fileUrl || !chatFileActions?.fileName || !relationship?.id) return;
        const targetFolder = chatSaveFolder === 'all' ? 'case_documents' : chatSaveFolder;

        setSavingChatFileToCase(true);
        try {
            const { data: existing, error: checkError } = await supabase
                .from('case_files')
                .select('id')
                .eq('relationship_id', relationship.id)
                .eq('file_url', chatFileActions.fileUrl)
                .eq('file_name', chatFileActions.fileName)
                .limit(1);

            if (checkError) throw checkError;

            if (existing?.length) {
                alert('This file is already in Case Files.');
                setChatFileActions(null);
                return;
            }

            const { error: saveError } = await supabase.from('case_files').insert([{
                relationship_id: relationship.id,
                uploaded_by: user.id,
                file_name: chatFileActions.fileName,
                file_url: chatFileActions.fileUrl,
                file_type: inferMimeTypeFromName(chatFileActions.fileName),
                folder: targetFolder,
            }]);

            if (saveError) throw saveError;

            await loadFiles();
            alert('File saved to Case Files.');
            setChatFileActions(null);
        } catch (err) {
            alert('Could not save file: ' + err.message);
        } finally {
            setSavingChatFileToCase(false);
        }
    };

    const openFile = (fileUrl) => { if (!fileUrl) return; window.open(fileUrl, '_blank', 'noopener,noreferrer'); };

    const confirmDeleteFile = async () => {
        if (!pendingDeleteFile?.id) return;
        await supabase.from('case_files').delete().eq('id', pendingDeleteFile.id);
        await loadFiles();
        setPendingDeleteFile(null);
    };

    const loadExistingRating = async () => {
        const { data } = await supabase.from('ratings').select('*').eq('lawyer_id', relationship.lawyer_id).eq('client_id', user.id).single();
        if (data) { setExistingRating(data); setRatingStars(data.stars); setRatingReview(data.review || ''); }
    };

    const submitRating = async () => {
        if (!ratingStars) return;
        const payload = { lawyer_id: relationship.lawyer_id, client_id: user.id, stars: ratingStars, review: ratingReview };
        if (existingRating) { await supabase.from('ratings').update(payload).eq('id', existingRating.id); }
        else { await supabase.from('ratings').insert([payload]); }
        setShowRating(false);
        await loadExistingRating();
    };

    const myName = myProfile?.full_name || user?.name || 'You';
    const activeFolderDef = getFolderById(activeFolder);

    return (
        <div className="cw-root">
            <div className={`cw-workspace ${chatOpen ? '' : 'cw-chat-collapsed-layout'}`}>
                <div className="cw-main">
                    {/* Header */}
                    <div className="cw-main-header">
                        <div className="cw-header-left">
                            <button className="cw-back-btn" onClick={onBack}><ArrowLeft size={13} /> Back</button>
                            <div className="cw-case-chip" data-tour="Case Header / Client Name">
                                <div className="cw-case-avatar"><MiniAvatar name={otherName} photoUrl={otherUser?.photo_url} /></div>
                                <div>
                                    <div className="cw-case-name">{otherName}</div>
                                    <div className="cw-case-sub">{otherRoleLabel} · {otherUser?.email || relationship.case_type || 'Active case'}</div>
                                </div>
                            </div>
                        </div>
                        <div className="cw-view-switch" role="tablist">
                            <button className={`cw-view-btn ${activeView === 'files' ? 'active' : ''}`} onClick={() => setActiveView('files')}>
                                <FolderOpen size={14} /> Files {fileCount > 0 && <span className="cw-nav-badge">{fileCount}</span>}
                            </button>
                            <button className={`cw-view-btn ${activeView === 'notes' ? 'active' : ''}`} onClick={() => setActiveView('notes')}>
                                <FileText size={14} /> Notepad {!notesSaved && <span className="cw-nav-badge">•</span>}
                            </button>
                        </div>
                        <div className="cw-header-actions">
                            {activeView === 'files' && (
                                <>
                                    <input type="file" ref={fileInputRef} onChange={handleFileSelected} hidden />
                                    <button className="cw-upload-btn" onClick={openUploadModal} disabled={uploading} data-tour="Upload Button (Upload icon or 'Upload Files' button)">
                                        <Upload size={14} /> {uploading ? 'Uploading...' : 'Upload'}
                                    </button>
                                </>
                            )}
                            {activeView === 'notes' && (
                                <button className="cw-save-btn" onClick={saveNotes} disabled={savingNotes || notesSaved}>
                                    {savingNotes ? 'Saving...' : 'Save Notes'}
                                </button>
                            )}
                            {userRole === 'client' && (
                                <button className="cw-rate-btn" onClick={() => setShowRating(true)}>
                                    <Star size={13} /> {existingRating ? 'Update Rating' : 'Rate Lawyer'}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="cw-content-area">
                        {activeView === 'files' && (
                            <div className="cw-files-layout">
                                {/* Folder Sidebar */}
                                <aside className="cw-folder-sidebar" data-tour="Folder Tabs">
                                    <div className="cw-folder-sidebar-title"><FolderClosed size={12} /> Folders</div>
                                    {FOLDERS.map(folder => {
                                        const Icon = folder.icon;
                                        const count = folderCounts[folder.id] || 0;
                                        const isActive = activeFolder === folder.id;
                                        return (
                                            <button key={folder.id}
                                                className={`cw-folder-item ${isActive ? 'active' : ''}`}
                                                onClick={() => setActiveFolder(folder.id)}
                                            >
                                                <span className="cw-folder-icon" style={{ color: isActive ? folder.color : '#8fa0ba', background: isActive ? folder.bg : 'transparent' }}>
                                                    <Icon size={14} />
                                                </span>
                                                <span className="cw-folder-label">{folder.label}</span>
                                                {count > 0 && <span className="cw-folder-count" style={isActive ? { background: folder.color } : {}}>{count}</span>}
                                            </button>
                                        );
                                    })}
                                </aside>

                                {/* Files Panel */}
                                <div className="cw-files-panel">
                                    <div className="cw-folder-header">
                                        <div className="cw-folder-header-icon" style={{ color: activeFolderDef.color, background: activeFolderDef.bg }}>
                                            {React.createElement(activeFolderDef.icon, { size: 16 })}
                                        </div>
                                        <div>
                                            <div className="cw-folder-header-name">{activeFolderDef.label}</div>
                                            <div className="cw-folder-header-desc">{activeFolderDef.description}</div>
                                        </div>
                                        <div className="cw-folder-header-count">{folderCounts[activeFolder]} file{folderCounts[activeFolder] !== 1 ? 's' : ''}</div>
                                    </div>

                                    <div className="cw-files-toolbar">
                                        <div className="cw-search-wrap">
                                            <Search size={14} />
                                            <input value={fileSearch} onChange={(e) => setFileSearch(e.target.value)} placeholder="Search files" />
                                        </div>
                                        <button type="button" className="cw-filter-btn"><SlidersHorizontal size={13} /> Filter</button>
                                    </div>

                                    <div className="cw-files-grid" data-tour="File List / Document Row">
                                        {filteredFiles.length === 0 ? (
                                            <div className="cw-files-empty">
                                                <div className="cw-files-empty-icon" aria-hidden="true"><div className="cw-empty-file-outline" /></div>
                                                <h3>{folderFiles.length === 0 ? `No files in ${activeFolderDef.label}` : 'No matching files'}</h3>
                                                <p>{folderFiles.length === 0
                                                    ? activeFolderDef.description + '. Click Upload to add one.'
                                                    : 'Try a different keyword for your file search'}</p>
                                            </div>
                                        ) : filteredFiles.map((f, idx) => (
                                            <div key={f.id}
                                                className={`cw-file-card ${getTypeClass(f.file_type)} cw-accent-${(idx % 4) + 1}`}
                                                onClick={() => openFile(f.file_url)}
                                                role="button" tabIndex={0}
                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFile(f.file_url); } }}
                                            >
                                                {activeFolder === 'all' && f.folder && (() => {
                                                    const fd = getFolderById(f.folder);
                                                    return (
                                                        <span className="cw-file-folder-tag" style={{ color: fd.color, background: fd.bg }}>
                                                            {React.createElement(fd.icon, { size: 10 })} {fd.label}
                                                        </span>
                                                    );
                                                })()}
                                                <div className="cw-file-thumb"><div className="cw-file-outline" /></div>
                                                <div className="cw-file-card-name">{f.file_name}</div>
                                                <div className="cw-file-card-meta">{formatBytes(f.file_size)}</div>
                                                <div className="cw-file-card-date">{new Date(f.created_at).toLocaleDateString()}</div>
                                                <div className="cw-file-card-actions" data-tour="File Actions (Download, Star, Delete icons)">
                                                    <button
                                                        type="button"
                                                        className="cw-dl-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            downloadFile(f.file_url, f.file_name || 'file');
                                                        }}
                                                    >
                                                        <Download size={11} /> Download
                                                    </button>
                                                    {f.uploaded_by === user.id && (
                                                        <button className="cw-del-btn" onClick={(e) => { e.stopPropagation(); setPendingDeleteFile(f); }} title="Delete">
                                                            <Trash2 size={11} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeView === 'notes' && (
                            <div className="cw-notepad">
                                <div className="cw-notepad-bar">
                                    <FileText size={14} />
                                    <span>Your private notes for this case</span>
                                    {notesSaved && <div className="cw-saved-pill">✓ Saved</div>}
                                </div>
                                <div className="cw-notepad-toolbar" role="toolbar" aria-label="Notepad formatting options" data-tour="Notes Editor Toolbar">
                                    <select
                                        className="cw-note-select"
                                        value={editorFormat.block}
                                        onChange={(e) => applyBlockType(e.target.value)}
                                        title="Paragraph and heading styles"
                                        aria-label="Paragraph style"
                                    >
                                        <option value="p">Paragraph</option>
                                        <option value="h1">Heading 1</option>
                                        <option value="h2">Heading 2</option>
                                        <option value="h3">Heading 3</option>
                                    </select>
                                    <button
                                        type="button"
                                        className={`cw-note-tool-btn ${editorFormat.bold ? 'is-active' : ''}`}
                                        onClick={() => runEditorCommand('bold')}
                                        title="Bold"
                                        aria-pressed={editorFormat.bold}
                                    >
                                        <Bold size={13} />
                                    </button>
                                    <button
                                        type="button"
                                        className={`cw-note-tool-btn ${editorFormat.italic ? 'is-active' : ''}`}
                                        onClick={() => runEditorCommand('italic')}
                                        title="Italic"
                                        aria-pressed={editorFormat.italic}
                                    >
                                        <Italic size={13} />
                                    </button>
                                    <span className="cw-note-tool-divider" />
                                    <label className="cw-note-size-wrap" htmlFor="cw-note-size">Size</label>
                                    <select
                                        id="cw-note-size"
                                        className="cw-note-size-select"
                                        value={editorFormat.fontSize}
                                        onChange={(e) => applyTextSize(e.target.value)}
                                        title="Font size"
                                        aria-label="Font size"
                                    >
                                        <option value="1">1</option>
                                        <option value="2">2</option>
                                        <option value="3">3</option>
                                        <option value="4">4</option>
                                        <option value="5">5</option>
                                        <option value="6">6</option>
                                        <option value="7">7</option>
                                    </select>
                                </div>
                                <div
                                    ref={noteEditorRef}
                                    className="cw-notepad-editor"
                                    contentEditable
                                    suppressContentEditableWarning
                                    onInput={() => { syncNotesFromEditor(); updateEditorFormatState(); }}
                                    onFocus={() => setIsNoteFocused(true)}
                                    onBlur={() => { setIsNoteFocused(false); syncNotesFromEditor(); updateEditorFormatState(); }}
                                    onKeyUp={updateEditorFormatState}
                                    onMouseUp={updateEditorFormatState}
                                    data-empty={notesEmpty ? 'true' : 'false'}
                                    data-show-placeholder={notesEmpty && !isNoteFocused ? 'true' : 'false'}
                                    data-placeholder={`Write your findings, key dates, observations...\n\nOnly you can see these notes.`}
                                    data-tour="Notes/Findings Section (Text Editor area)"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Chat Panel */}
                <div className={`cw-chat-panel ${chatOpen ? '' : 'collapsed'}`}>
                    <button
                        className="cw-chat-collapse-btn"
                        onClick={() => setChatOpen(o => !o)}
                        title={chatOpen ? 'Collapse messages' : 'Expand messages'}
                        aria-label={chatOpen ? 'Collapse messages' : 'Expand messages'}
                    >
                        {chatOpen ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                    </button>
                    <div className="cw-chat-header">
                        <MessageSquare size={14} style={{ color: '#8B96A8', flexShrink: 0 }} />
                        {chatOpen && <span className="cw-chat-title">Case Messages</span>}
                    </div>
                    {chatOpen && (
                        <>
                            <div className="cw-chat-msgs" data-tour="Client Communication Section (Chat/Messages area)">
                                {chatMessages.length === 0 && (
                                    <div style={{ textAlign: 'center', color: '#B0BAC9', padding: '40px 16px', fontSize: 13 }}>
                                        No messages yet — discuss this case here
                                    </div>
                                )}
                                {chatMessages.map(msg => {
                                    const isMine = msg.sender_id === user.id;
                                    const senderName = isMine ? myName : otherName;
                                    const senderPhoto = isMine ? myProfile?.photo_url : otherUser?.photo_url;
                                    return (
                                        <div key={msg.id} className={`cw-msg-wrap ${isMine ? 'mine' : 'theirs'}`}>
                                            {!isMine && (
                                                <div className="cw-msg-sender">
                                                    <MiniAvatar name={senderName} photoUrl={senderPhoto} />
                                                    <span className="cw-msg-sender-name">{senderName}</span>
                                                </div>
                                            )}
                                            <div className={`cw-msg-bubble ${isMine ? 'mine' : 'theirs'}`}>
                                                {msg.content && <div>{msg.content}</div>}
                                                {msg.file_url && (
                                                    <button type="button" className="cw-file-link" onClick={() => openChatFileActions(msg)}>
                                                        <File size={11} /> {msg.file_name || 'File'}
                                                    </button>
                                                )}
                                            </div>
                                            <span className="cw-msg-time">{timeAgo(msg.created_at)}</span>
                                        </div>
                                    );
                                })}
                                <div ref={bottomRef} />
                            </div>
                            <div className="cw-chat-input-row">
                                <input type="file" ref={chatFileRef} onChange={uploadChatFile} hidden />
                                <div className="cw-chat-input-pill" data-tour="Message Input Box (Text input for messages)">
                                    <input value={chatText} onChange={e => setChatText(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendChat(chatText)}
                                        placeholder="Type your message..." disabled={uploading} />
                                    <div className="cw-chat-input-bottom">
                                        <button className="cw-chat-action-btn" onClick={openChatAttachModal} disabled={uploading || chatSendingCaseFile} title="Attach file">
                                            <Paperclip size={15} />
                                        </button>
                                        <button className="cw-chat-send-btn" onClick={() => sendChat(chatText)} disabled={!chatText.trim()}>
                                            <Send size={13} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Upload Folder Picker Modal */}
            {showUploadModal && (
                <div className="cw-modal-overlay" onClick={() => setShowUploadModal(false)}>
                    <div className="cw-modal cw-upload-modal" onClick={e => e.stopPropagation()}>
                        <h3>Upload File</h3>
                        {activeFolder === 'all' ? (
                            <>
                                <div className="cw-upload-folder-label">Which type of file is this, and where should we upload it?</div>
                                <div className="cw-folder-picker">
                                    {FOLDERS.filter(f => f.id !== 'all').map(folder => {
                                        const Icon = folder.icon;
                                        const isSelected = uploadFolder === folder.id;
                                        return (
                                            <button key={folder.id}
                                                className={`cw-folder-pick-btn ${isSelected ? 'selected' : ''}`}
                                                style={isSelected ? { borderColor: folder.color, background: folder.bg } : {}}
                                                onClick={() => setUploadFolder(folder.id)}
                                            >
                                                <span className="cw-folder-pick-icon" style={{ color: folder.color, background: folder.bg }}><Icon size={15} /></span>
                                                <span className="cw-folder-pick-name">{folder.label}</span>
                                                {isSelected && <span className="cw-folder-pick-check" style={{ color: folder.color }}>✓</span>}
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        ) : (
                            <div className="cw-upload-folder-label">Files from this view will upload to {getFolderById(uploadFolder).label}.</div>
                        )}
                        <div className="cw-modal-btns">
                            <button className="cw-submit-btn" onClick={triggerUploadFilePicker}>Upload</button>
                            <button className="cw-cancel-btn" onClick={() => setShowUploadModal(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Chat Attach Source Modal */}
            {showChatAttachModal && (
                <div className="cw-modal-overlay" onClick={() => setShowChatAttachModal(false)}>
                    <div className="cw-modal cw-chat-attach-modal" onClick={e => e.stopPropagation()}>
                        <h3>Attach File</h3>
                        {chatAttachMode === 'choose' && (
                            <>
                                <p>Choose where you want to send the file from.</p>
                                <div className="cw-chat-source-grid">
                                    <button className="cw-chat-source-btn" onClick={openLocalChatUpload}>
                                        <span className="cw-chat-source-icon"><Upload size={15} /></span>
                                        <span className="cw-chat-source-title">From Local PC</span>
                                        <span className="cw-chat-source-sub">Upload a new file from your computer</span>
                                    </button>
                                    <button className="cw-chat-source-btn" onClick={() => setChatAttachMode('case_files')}>
                                        <span className="cw-chat-source-icon"><FolderOpen size={15} /></span>
                                        <span className="cw-chat-source-title">From Case Files</span>
                                        <span className="cw-chat-source-sub">Send an existing file from this case</span>
                                    </button>
                                </div>
                            </>
                        )}

                        {chatAttachMode === 'case_files' && (
                            <>
                                <div className="cw-chat-picker-header">
                                    <button className="cw-chat-picker-back" onClick={() => setChatAttachMode('choose')}>← Back</button>
                                    <span>Select from Case Files</span>
                                </div>
                                <div className="cw-search-wrap cw-chat-file-search">
                                    <Search size={14} />
                                    <input
                                        value={chatAttachSearch}
                                        onChange={(e) => setChatAttachSearch(e.target.value)}
                                        placeholder="Search case files"
                                    />
                                </div>
                                <div className="cw-chat-file-list" role="listbox" aria-label="Case files for sending">
                                    {chatAttachFiles.length === 0 ? (
                                        <div className="cw-chat-file-empty">No case files found.</div>
                                    ) : chatAttachFiles.map((f) => (
                                        <button
                                            key={f.id}
                                            className={`cw-chat-file-item ${String(selectedChatCaseFileId) === String(f.id) ? 'selected' : ''}`}
                                            onClick={() => setSelectedChatCaseFileId(f.id)}
                                        >
                                            <span className="cw-chat-file-item-name">{f.file_name}</span>
                                            <span className="cw-chat-file-item-meta">{formatBytes(f.file_size) || 'File'} · {new Date(f.created_at).toLocaleDateString()}</span>
                                        </button>
                                    ))}
                                </div>
                                <div className="cw-modal-btns">
                                    <button
                                        className="cw-submit-btn"
                                        onClick={sendExistingCaseFileToChat}
                                        disabled={!selectedChatCaseFile || chatSendingCaseFile}
                                    >
                                        {chatSendingCaseFile ? 'Sending...' : 'Send File'}
                                    </button>
                                    <button className="cw-cancel-btn" onClick={() => setShowChatAttachModal(false)}>Cancel</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Chat Received File Actions Modal */}
            {chatFileActions && (
                <div className="cw-modal-overlay" onClick={() => setChatFileActions(null)}>
                    <div className="cw-modal cw-chat-file-actions-modal" onClick={e => e.stopPropagation()}>
                        <h3>File Options</h3>
                        <p>{chatFileActions.fileName}</p>
                        <div className="cw-modal-btns cw-chat-file-actions-btns">
                            <button className="cw-submit-btn" onClick={downloadChatFileToPc}>
                                <Download size={13} /> Download to PC
                            </button>
                        </div>
                        <div className="cw-chat-save-box">
                            <div className="cw-upload-folder-label">Save into Case Files</div>
                            <div className="cw-chat-save-row">
                                <select
                                    className="cw-note-select cw-chat-folder-select"
                                    value={chatSaveFolder}
                                    onChange={(e) => setChatSaveFolder(e.target.value)}
                                >
                                    {FOLDERS.filter(f => f.id !== 'all').map(folder => (
                                        <option key={folder.id} value={folder.id}>{folder.label}</option>
                                    ))}
                                </select>
                                <button className="cw-submit-btn" onClick={saveChatFileToCaseFiles} disabled={savingChatFileToCase}>
                                    {savingChatFileToCase ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </div>
                        <div className="cw-modal-btns">
                            <button className="cw-cancel-btn" onClick={() => setChatFileActions(null)}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rating Modal */}
            {showRating && (
                <div className="cw-modal-overlay" onClick={() => setShowRating(false)}>
                    <div className="cw-modal" onClick={e => e.stopPropagation()}>
                        <h3>Rate {otherName}</h3>
                        <p>Share your experience working with this lawyer</p>
                        <div className="cw-stars-row">
                            {[1, 2, 3, 4, 5].map(s => (
                                <button key={s} onMouseEnter={() => setRatingHover(s)} onMouseLeave={() => setRatingHover(0)} onClick={() => setRatingStars(s)}>
                                    {s <= (ratingHover || ratingStars) ? '⭐' : '☆'}
                                </button>
                            ))}
                        </div>
                        <textarea className="cw-review-input" rows={3} placeholder="Write a review (optional)..." value={ratingReview} onChange={e => setRatingReview(e.target.value)} />
                        <div className="cw-modal-btns">
                            <button className="cw-submit-btn" onClick={submitRating} disabled={!ratingStars}>{existingRating ? 'Update Rating' : 'Submit Rating'}</button>
                            <button className="cw-cancel-btn" onClick={() => setShowRating(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {pendingDeleteFile && (
                <div className="cw-modal-overlay" onClick={() => setPendingDeleteFile(null)}>
                    <div className="cw-modal cw-delete-modal" onClick={e => e.stopPropagation()}>
                        <h3>Delete File?</h3>
                        <p>Are you sure you want to delete <strong>{pendingDeleteFile.file_name}</strong>? This action cannot be undone.</p>
                        <div className="cw-modal-btns">
                            <button className="cw-delete-confirm-btn" onClick={confirmDeleteFile}>Delete</button>
                            <button className="cw-delete-cancel-btn" onClick={() => setPendingDeleteFile(null)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}